import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { configApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import InputBase from '@material-ui/core/InputBase';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import SearchIcon from '@material-ui/icons/Search';
import DnsIcon from '@material-ui/icons/Dns';
import DescriptionIcon from '@material-ui/icons/Description';
import { makeStyles } from '@material-ui/core/styles';
import { listMyVms, listRequests } from '../../plugins/infrastructure/api';
import { VmInstance, VmRequest } from '../../plugins/infrastructure/types';

const MAX_RESULTS = 8;
const DEBOUNCE_MS = 200;

type Result =
  | { kind: 'vm'; key: string; label: string; sublabel: string; path: string }
  | {
      kind: 'request';
      key: string;
      label: string;
      sublabel: string;
      path: string;
    };

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    // Fills the TopBar's center flex region edge-to-edge, matching the
    // reference design — the earlier fixed maxWidth (480, then 720) left a
    // visible gap on both sides once the flex container was wider than
    // that cap, which is what still looked "not full width" next to it.
    width: '100%',
  },
  inputWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 1.5),
  },
  input: {
    flex: 1,
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
  },
  kbd: {
    fontSize: '0.6875rem',
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '1px 6px',
    flexShrink: 0,
  },
  results: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    zIndex: theme.zIndex.modal,
    maxHeight: 360,
    overflowY: 'auto',
  },
}));

// A real, working search over data this frontend already has access to (the
// caller's own VMs and requests) — not a backend search-index integration
// (no collator exists for these resource types today), so results are
// necessarily scoped to "my VMs" / "my requests", fetched fresh per query
// rather than claiming to search anything broader.
export const TopBarSearch = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendUrl = configApi.getString('backend.baseUrl');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const trimmed = query.trim().toLowerCase();
    if (trimmed.length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      Promise.all([
        listMyVms(fetchApi, backendUrl, 'mine'),
        listRequests(fetchApi, backendUrl, { mine: true }),
      ])
        .then(([vmsResult, requests]) => {
          if (cancelled) return;
          const vmMatches: Result[] = vmsResult.instances
            .filter((vm: VmInstance) =>
              vm.vmName.toLowerCase().includes(trimmed),
            )
            .slice(0, MAX_RESULTS)
            .map((vm: VmInstance) => ({
              kind: 'vm' as const,
              key: vm.id,
              label: vm.vmName,
              sublabel: `${vm.region} — ${vm.powerState}`,
              path: `/infrastructure/instances/${vm.id}`,
            }));
          const requestMatches: Result[] = requests
            .filter(
              (r: VmRequest) =>
                r.vmName.toLowerCase().includes(trimmed) ||
                r.id.toLowerCase().includes(trimmed),
            )
            .slice(0, MAX_RESULTS)
            .map((r: VmRequest) => ({
              kind: 'request' as const,
              key: r.id,
              label: r.vmName,
              sublabel: `Request — ${r.status}`,
              path: `/infrastructure/requests/${r.id}`,
            }));
          setResults([...vmMatches, ...requestMatches].slice(0, MAX_RESULTS));
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, fetchApi, backendUrl]);

  const handleSelect = (result: Result) => {
    navigate(result.path);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className={classes.root} ref={containerRef}>
      <div className={classes.inputWrapper}>
        <SearchIcon fontSize="small" color="disabled" />
        <InputBase
          inputRef={inputRef}
          className={classes.input}
          placeholder="Search VMs, requests..."
          value={query}
          onFocus={() => setOpen(true)}
          onChange={e => {
            setQuery(e.target.value);
            setOpen(true);
          }}
        />
        <span className={classes.kbd}>⌘K</span>
      </div>

      {open && query.trim().length >= 2 && (
        <Paper className={classes.results} elevation={4}>
          {loading && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ padding: 12 }}
            >
              Searching…
            </Typography>
          )}
          {!loading && results.length === 0 && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ padding: 12 }}
            >
              No matching VMs or requests
            </Typography>
          )}
          {!loading && results.length > 0 && (
            <List dense>
              {results.map(result => (
                <ListItem
                  key={`${result.kind}-${result.key}`}
                  button
                  onClick={() => handleSelect(result)}
                >
                  <ListItemIcon style={{ minWidth: 32 }}>
                    {result.kind === 'vm' ? (
                      <DnsIcon fontSize="small" />
                    ) : (
                      <DescriptionIcon fontSize="small" />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={result.label}
                    secondary={result.sublabel}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      )}
    </div>
  );
};
